import { useQuery } from '@tanstack/react-query'
import { Alert } from '@/components/console'
import { buttonClass } from '@/components/console/classes'
import { billingQuery, checkoutPath, planFromParam } from '@/lib/api/queries'

/** A provider trial this close to its end is warm, not just informational. */
const SOON_DAYS = 3

/**
 * Above every dashboard screen: where the account's plan stands, and the one thing to do about it.
 *
 * Nothing is shown for a paid plan, or while the answer is loading or unavailable: this is a
 * prompt, and a prompt that flickers or errors loudly is worse than none. The free tier (the plan
 * they chose, before paying) says setup and testing are free and that going live needs payment;
 * a trial a provider runs says how long is left; an account with no plan is offered one. "Add
 * payment" goes through the API's checkout, which returns the person to the app, where the plan
 * is attached to this same account. A failed or abandoned card changes nothing: they stay free.
 */
export function PlanBanner() {
  const billing = useQuery(billingQuery)
  const b = billing.data
  if (!b || b.paid) return null

  const plan = planFromParam(b.plan_key) ?? 'growth'
  const pay = (
    <a className={buttonClass({ variant: 'primary', size: 'xs' })} href={checkoutPath(plan)}>
      Add payment
    </a>
  )

  if (!b.has_plan) {
    return (
      <Alert tone="info" title="You don’t have a plan yet." action={pay} style={{ marginBottom: 14 }}>
        Choose one to go live and have your calls answered.
      </Alert>
    )
  }
  if (b.status === 'free' || !b.entitled) {
    return (
      <Alert tone="info" title={`You’re on the free ${b.plan_name} plan.`} action={pay} style={{ marginBottom: 14 }}>
        Set up your receptionist and test it for free. Add payment to go live and have real calls answered.
      </Alert>
    )
  }
  const left = b.trial_days_left ?? 0
  return (
    <Alert
      tone={left <= SOON_DAYS ? 'warn' : 'info'}
      title={`${b.plan_name} trial: ${left} ${left === 1 ? 'day' : 'days'} left.`}
      action={pay}
      style={{ marginBottom: 14 }}
    >
      Add payment any time to keep your plan.
    </Alert>
  )
}
